import { useState } from 'react'

import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { BN } from 'fbonds-core'
import { BANX_TOKEN_MINT } from 'fbonds-core/lib/fbond-protocol/constants'
import { chain, uniqueId } from 'lodash'
import { TxnExecutor } from 'solana-transactions-executor'

import { useTabs } from '@banx/components/Tabs'

import { useCollateralsList } from '@banx/hooks'
import { useModal } from '@banx/store/common'
import {
  TXN_EXECUTOR_DEFAULT_OPTIONS,
  createExecutorWalletAndConnection,
  defaultTxnErrorHandler,
} from '@banx/transactions'
import {
  createStakeBanxTokenTxnData,
  createUnstakeBanxTokenTxnData,
  parseAnyStakingSimulatedAccounts,
} from '@banx/transactions/staking'
import {
  ZERO_BN,
  bnToHuman,
  destroySnackbar,
  enqueueConfirmationError,
  enqueueSnackbar,
  enqueueTransactionSent,
  enqueueWaitingConfirmation,
  limitDecimalPlaces,
} from '@banx/utils'

import { calcPartnerPoints } from '../../helpers'
import { useBanxStakeInfo } from '../../hooks'
import { convertStakingSimulatedAccountsToMergeData } from '../../optimistics'
import { calcIdleBalance, calcPlayerPoints, formatBanxTokensStrToBN } from './helpers'

export const useStakeTokensModal = () => {
  const { banxStakeInfo } = useBanxStakeInfo()

  const { value: currentTabValue, ...tabProps } = useTabs({
    tabs: TABS,
    defaultValue: ModalTab.Stake,
  })

  const { collateralsList } = useCollateralsList()
  const banxToken = collateralsList?.find(
    (collateral) => collateral.collateral.mint === BANX_TOKEN_MINT.toBase58(),
  )

  const isStakeTab = currentTabValue === ModalTab.Stake

  const tokensPerPartnerPointsBN = banxStakeInfo?.settings?.tokensPerPartnerPoints ?? ZERO_BN
  const banxWalletBalanceBN = banxToken?.amountInWallet ?? ZERO_BN

  const banxWalletBalance = bnToHuman(banxWalletBalanceBN)
  const totalTokenStaked = bnToHuman(banxStakeInfo?.tokenStake?.tokensStaked ?? ZERO_BN)

  const [inputTokenAmount, setInputTokenAmount] = useState('')

  const handleChangeValue = (inputValue: string) => {
    setInputTokenAmount(limitDecimalPlaces(inputValue))
  }

  const onSetMax = () => {
    if (isStakeTab) {
      return setInputTokenAmount(limitDecimalPlaces(banxWalletBalance.toString()))
    }
    return setInputTokenAmount(limitDecimalPlaces(totalTokenStaked.toString()))
  }

  const onTabClick = () => {
    setInputTokenAmount('')
  }

  const idleBanxWalletBalance = calcIdleBalance(banxWalletBalance, inputTokenAmount)
  const idleStakedTokens = calcIdleBalance(totalTokenStaked, inputTokenAmount)

  const partnerPoints = calcPartnerPoints(
    formatBanxTokensStrToBN(inputTokenAmount),
    tokensPerPartnerPointsBN,
  )
  const playerPoints = calcPlayerPoints(inputTokenAmount)

  const parsedInputTokenAmount = parseFloat(inputTokenAmount) || 0

  const isInsufficientFundsToStake = parsedInputTokenAmount > banxWalletBalance
  const isInsufficientFundsToUnstake = parsedInputTokenAmount > totalTokenStaked

  const isStakeDisabled = !parsedInputTokenAmount || isInsufficientFundsToStake
  const isUnstakeDisabled = !parsedInputTokenAmount || isInsufficientFundsToUnstake

  const showErrorMessage = isStakeTab ? isInsufficientFundsToStake : isInsufficientFundsToUnstake

  return {
    onSetMax,
    handleChangeValue,
    isUnstakeDisabled,
    isStakeDisabled,
    idleStakedTokens,
    banxWalletBalance,
    banxWalletBalanceBN,
    partnerPoints,
    playerPoints,
    idleBanxWalletBalance,
    inputTokenAmount,
    currentTabValue,
    tabProps,
    onTabClick,
    totalTokenStaked,
    showErrorMessage,
    isStakeTab,
  }
}

type UseTokenTransactionsParams = {
  inputTokenAmount: BN
  banxWalletBalance: BN
}

export const useTokenTransactions = ({ inputTokenAmount }: UseTokenTransactionsParams) => {
  const wallet = useWallet()
  const { connection } = useConnection()
  const { close: closeModal } = useModal()
  const { banxStakeInfo, setOptimistic: setBanxStakeInfoOptimistic } = useBanxStakeInfo()

  const onStake = async () => {
    const loadingSnackbarId = uniqueId()

    try {
      const walletAndConnection = createExecutorWalletAndConnection({ wallet, connection })

      const txnData = await createStakeBanxTokenTxnData(
        { tokensToStake: inputTokenAmount, createStakeAccount: !banxStakeInfo?.subscription },
        walletAndConnection,
      )

      await new TxnExecutor(walletAndConnection, TXN_EXECUTOR_DEFAULT_OPTIONS)
        .addTxnData(txnData)
        .on('sentSome', (results) => {
          results.forEach(({ signature }) => enqueueTransactionSent(signature))
          enqueueWaitingConfirmation(loadingSnackbarId)
        })
        .on('confirmedAll', (results) => {
          const { confirmed, failed } = results

          destroySnackbar(loadingSnackbarId)

          if (failed.length) {
            return failed.forEach(({ signature, reason }) =>
              enqueueConfirmationError(signature, reason),
            )
          }

          return confirmed.forEach(({ accountInfoByPubkey, signature }) => {
            if (!accountInfoByPubkey) return

            enqueueSnackbar({
              message: 'Tokens successfully staked',
              type: 'success',
              solanaExplorerPath: `tx/${signature}`,
            })

            const optimisticParams = chain(confirmed)
              .map(({ accountInfoByPubkey }) => {
                if (!accountInfoByPubkey) return null
                return parseAnyStakingSimulatedAccounts(accountInfoByPubkey)
              })
              .compact()
              .thru(convertStakingSimulatedAccountsToMergeData)
              .value()

            setBanxStakeInfoOptimistic(wallet.publicKey!.toBase58(), optimisticParams)

            closeModal()
          })
        })
        .on('error', (error) => {
          throw error
        })
        .execute()
    } catch (error) {
      destroySnackbar(loadingSnackbarId)
      defaultTxnErrorHandler(error, {
        additionalData: inputTokenAmount,
        walletPubkey: wallet?.publicKey?.toBase58(),
        transactionName: 'StakeBanxTokens',
      })
    }
  }

  const onUnstake = async () => {
    const loadingSnackbarId = uniqueId()

    try {
      const walletAndConnection = createExecutorWalletAndConnection({ wallet, connection })

      const txnData = await createUnstakeBanxTokenTxnData(
        { tokensToUnstake: inputTokenAmount },
        walletAndConnection,
      )

      new TxnExecutor(walletAndConnection, TXN_EXECUTOR_DEFAULT_OPTIONS)
        .addTxnData(txnData)
        .on('sentSome', (results) => {
          results.forEach(({ signature }) => enqueueTransactionSent(signature))
          enqueueWaitingConfirmation(loadingSnackbarId)
        })
        .on('confirmedAll', (results) => {
          const { confirmed, failed } = results

          destroySnackbar(loadingSnackbarId)

          if (failed.length) {
            return failed.forEach(({ signature, reason }) =>
              enqueueConfirmationError(signature, reason),
            )
          }

          return confirmed.forEach(({ accountInfoByPubkey, signature }) => {
            if (!accountInfoByPubkey) return

            enqueueSnackbar({
              message: 'Tokens successfully unstaked',
              type: 'success',
              solanaExplorerPath: `tx/${signature}`,
            })

            const optimisticParams = chain(confirmed)
              .map(({ accountInfoByPubkey }) => {
                if (!accountInfoByPubkey) return null
                return parseAnyStakingSimulatedAccounts(accountInfoByPubkey)
              })
              .compact()
              .thru(convertStakingSimulatedAccountsToMergeData)
              .value()

            setBanxStakeInfoOptimistic(wallet.publicKey!.toBase58(), optimisticParams)
            closeModal()
          })
        })
        .on('error', (error) => {
          throw error
        })
        .execute()
    } catch (error) {
      destroySnackbar(loadingSnackbarId)
      defaultTxnErrorHandler(error, {
        additionalData: inputTokenAmount,
        walletPubkey: wallet?.publicKey?.toBase58(),
        transactionName: 'UnstakeBanxTokens',
      })
    }
  }

  return { onStake, onUnstake }
}

enum ModalTab {
  Stake = 'stake',
  Unstake = 'unstake',
}

const TABS = [
  {
    label: 'Stake',
    value: ModalTab.Stake,
  },
  {
    label: 'Unstake',
    value: ModalTab.Unstake,
  },
]
